/**
 * /livret-plateau — Livre interactif public
 *
 * Reprend fidèlement les 7 chapitres du Livret CODE 2 ROUES Premium :
 * 01 Bienvenue · 02 Moto/Équipement/Commandes · 03 Parcours · 04 M1-M3 · 05 M4-M6 · 06 Progression · 07 Prêt ?
 *
 * Accessible en public. Éléments interactifs cochables sauvegardés en localStorage.
 * Bouton téléchargement du PDF officiel.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Download, ChevronLeft, ChevronRight, Menu, X, Home,
  Eye, Feather, Scale, Repeat, CheckCircle2, Circle, AlertCircle,
  Wind, Target, Trophy, Sparkles, Bike, Route, Zap, Users, Shield,
  QrCode,
} from 'lucide-react';
import TrameLivretCross from '../../components/trames/TrameLivretCross';
import SaveProgressCTA from '../../components/livrets/SaveProgressCTA';
import LivretQrShareModal from '../../components/livrets/LivretQrShareModal';
import {
  loadPlateauAutoEval,
  computeProgFromAutoEval,
  summarizeProgUpdates,
} from '../../utils/livretAutoEvalMap';
import {
  applyLivretToPlateauAutoEval,
  previewLivretToTrameSync,
} from '../../utils/livretToTrameSync';
import { toast } from 'sonner';

const STORAGE_KEY = 'code2roues:livret-book:v1';
const PDF_URL = '/livrets/livret-plateau-a2-code2roues.pdf';

const CHAPTERS = [
  { id: 'cover',   n: '',   title: 'Couverture',                          icon: Bike,      color: '#ef4444' },
  { id: 'ch01',    n: '01', title: 'Bienvenue sur le plateau',            icon: Sparkles,  color: '#f97316' },
  { id: 'ch02',    n: '02', title: 'Moto · Équipement · Commandes',       icon: Shield,    color: '#eab308' },
  { id: 'ch03',    n: '03', title: 'Ton parcours plateau',                icon: Route,     color: '#22c55e' },
  { id: 'ch04',    n: '04', title: 'Manœuvres 1 à 3',                     icon: Zap,       color: '#06b6d4' },
  { id: 'ch05',    n: '05', title: 'Manœuvres 4 à 6',                     icon: Users,     color: '#6366f1' },
  { id: 'ch06',    n: '06', title: 'Ma progression',                      icon: Target,    color: '#a855f7' },
  { id: 'ch07',    n: '07', title: 'Prêt pour l\'examen ?',               icon: Trophy,    color: '#ec4899' },
];

/**
 * Keys d'items cochables par chapitre — utilisé pour calculer la
 * progression par chapitre (badge sommaire + CTA fin de chapitre).
 * Un chapitre est considéré "complet" quand tous ses items sont validés.
 */
const CHAPTER_ITEM_KEYS = {
  cover: [], // Pas d'items à cocher sur la couverture
  ch01: ['r-regard', 'r-douceur', 'r-equilibre', 'r-regularite'],
  ch02: ['e-casque', 'e-blouson', 'e-gants', 'e-pantalon', 'e-chaussures', 'c-mg', 'c-pg', 'c-md', 'c-pd'],
  ch03: ['parcours-memoire'], // Radio → "done" si valeur non nulle
  ch04: ['m-01', 'm-02', 'm-03'],
  ch05: ['m-04', 'm-05', 'm-06'],
  ch06: ['prog-0', 'prog-1', 'prog-2', 'prog-3', 'prog-4', 'prog-5', 'prog-6', 'prog-7', 'prog-8', 'prog-9', 'prog-10', 'prog-11', 'pointfort', 'objectif'],
  ch07: ['x-ordre', 'x-regard', 'x-cmds', 'x-focus', 'x-respire'],
};

/**
 * Compte le nombre d'items validés dans un chapitre.
 * - checkbox   → state[key] === true
 * - status     → state[key] existe et ≠ 'none' (chapter 06 prog-*)
 * - texte      → state[key] non vide (pointfort, objectif)
 * - radio      → state[key] non nul (parcours-memoire)
 */
function isItemDone(state, key) {
  const v = state[key];
  if (v === true) return true;
  if (v === false || v === null || v === undefined) return false;
  if (typeof v === 'string') {
    if (v === '' || v === 'none') return false;
    return true;
  }
  return !!v;
}

function chapterProgress(state, chapterId) {
  const keys = CHAPTER_ITEM_KEYS[chapterId] || [];
  const total = keys.length;
  const done = keys.filter(k => isItemDone(state, k)).length;
  return { done, total, complete: total > 0 && done === total };
}

function loadState() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); }
  catch (_) { return {}; }
}
function saveState(state) { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); }

export default function LivretPlateauBookPublic() {
  const navigate = useNavigate();
  // Deep-link : ?chapter=<id> permet à un QR de renvoyer sur le bon chapitre
  const initialChapterIdx = (() => {
    if (typeof window === 'undefined') return 0;
    const p = new URLSearchParams(window.location.search);
    const wanted = p.get('chapter');
    if (!wanted) return 0;
    const idx = CHAPTERS.findIndex(c => c.id === wanted);
    return idx >= 0 ? idx : 0;
  })();
  const [chapterIdx, setChapterIdx] = useState(initialChapterIdx);
  const [state, setState] = useState({});
  const [menuOpen, setMenuOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [profil, setProfil] = useState({ eleve: '', moniteur: '', debut: '' });
  // Dismiss du CTA "chapitre suivant" pour ne pas re-spammer l'élève
  // après qu'il l'a fermé une fois pour un chapitre donné.
  const [dismissedNext, setDismissedNext] = useState({});

  useEffect(() => {
    document.title = 'Livret plateau A2 · Livre interactif · code2roues';
    const s = loadState();
    setState(s);
    if (s.__profil) setProfil(s.__profil);
  }, []);

  // Met à jour l'URL quand on change de chapitre — permet le partage
  // direct de la page et le retour arrière navigateur cohérent.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const p = new URLSearchParams(window.location.search);
    const currentQ = p.get('chapter');
    const wantedQ = CHAPTERS[chapterIdx]?.id;
    if (currentQ !== wantedQ && wantedQ) {
      p.set('chapter', wantedQ);
      const newUrl = window.location.pathname + '?' + p.toString();
      window.history.replaceState(null, '', newUrl);
    }
    // Scroll top à chaque changement (le contenu ci-dessous a du sens
    // seulement depuis le début).
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [chapterIdx]);

  const setValue = (key, value) => {
    setState((prev) => {
      const next = { ...prev, [key]: value };
      saveState(next);
      return next;
    });
  };
  const saveProfil = (p) => {
    setProfil(p);
    setState((prev) => {
      const next = { ...prev, __profil: p };
      saveState(next);
      return next;
    });
  };

  const current = CHAPTERS[chapterIdx];
  const prev = () => setChapterIdx((i) => Math.max(0, i - 1));
  const next = () => setChapterIdx((i) => Math.min(CHAPTERS.length - 1, i + 1));

  // Progression globale + par chapitre — utilisé pour badges + CTA fin de chapitre
  const perChapterProgress = useMemo(() => {
    const map = {};
    CHAPTERS.forEach(c => { map[c.id] = chapterProgress(state, c.id); });
    return map;
  }, [state]);

  const currentChapterProgress = perChapterProgress[current.id];
  const isLastChapter = chapterIdx === CHAPTERS.length - 1;
  const showNextChapterCTA =
    currentChapterProgress?.complete &&
    !isLastChapter &&
    !dismissedNext[current.id];

  const progression = useMemo(() => {
    const totals = Object.values(perChapterProgress).reduce(
      (acc, p) => ({ done: acc.done + p.done, total: acc.total + p.total }),
      { done: 0, total: 0 },
    );
    return totals;
  }, [perChapterProgress]);

  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      <TrameLivretCross
        current="livret"
        trameTo="/plateau-progression"
        trameLabel="Trame pédagogique Plateau A1/A2"
        livretTo="/livret-plateau"
        livretLabel="Livret Plateau interactif à cocher"
      />
      <SaveProgressCTA done={progression.done} />
      {/* Barre supérieure */}
      <header className="sticky top-0 z-30 bg-neutral-950/95 backdrop-blur border-b border-white/10">
        <div className="max-w-5xl mx-auto px-3 sm:px-4 h-14 flex items-center justify-between gap-1.5 sm:gap-2">
          <div className="flex items-center gap-1.5 sm:gap-2">
            <button
              onClick={() => setMenuOpen(true)}
              className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 h-9 rounded-lg bg-white/[0.05] hover:bg-white/[0.12] border border-white/10 text-white/85 text-[12px] font-bold transition"
              data-testid="btn-menu"
              aria-label="Ouvrir le sommaire"
            >
              <Menu size={14} /> <span className="hidden xs:inline">Sommaire</span><span className="xs:hidden">Menu</span>
            </button>
            <Link
              to="/"
              className="inline-flex items-center justify-center w-9 h-9 sm:w-auto sm:px-3 rounded-lg bg-white/[0.05] hover:bg-white/[0.12] border border-white/10 text-white/85 text-[12px] font-bold transition"
              data-testid="btn-home"
              aria-label="Retour à l'accueil Code2Roues"
            >
              <Home size={14} /> <span className="hidden sm:inline sm:ml-1.5">Accueil</span>
            </Link>
          </div>
          {/* Navigation chapitres — visible en permanence (top sticky) */}
          <div className="flex items-center gap-1" data-testid="top-nav-chapters">
            <button
              onClick={prev}
              disabled={chapterIdx === 0}
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-white/[0.05] hover:bg-white/[0.12] disabled:opacity-30 disabled:cursor-not-allowed border border-white/10 text-white/85 transition"
              data-testid="btn-prev-top"
              aria-label="Chapitre précédent"
            >
              <ChevronLeft size={16} />
            </button>
            <div className="hidden sm:block text-[10px] font-black uppercase tracking-widest text-white/40 px-1 tabular-nums">
              {chapterIdx}/{CHAPTERS.length - 1}
            </div>
            <button
              onClick={next}
              disabled={chapterIdx === CHAPTERS.length - 1}
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-white shadow-md disabled:opacity-30 disabled:cursor-not-allowed transition"
              style={{ background: chapterIdx === CHAPTERS.length - 1 ? '#333' : current.color }}
              data-testid="btn-next-top"
              aria-label="Chapitre suivant"
            >
              <ChevronRight size={16} />
            </button>
          </div>
          <div className="hidden md:block text-[9.5px] font-black uppercase tracking-[0.22em] text-white/60 truncate min-w-0 flex-1 text-center">
            {current.n && <span className="text-red-400">{current.n} · </span>}{current.title}
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <button
              type="button"
              onClick={() => setQrOpen(true)}
              className="inline-flex items-center justify-center w-9 h-9 sm:w-auto sm:px-3 rounded-lg bg-rose-500/20 hover:bg-rose-500/30 border border-rose-500/40 text-rose-200 text-[12px] font-black uppercase tracking-wider shadow-sm transition"
              data-testid="btn-qr-share"
              aria-label="Partager par QR code"
              title="Partager par QR code (élève sur son téléphone)"
            >
              <QrCode size={13} /> <span className="hidden sm:inline sm:ml-1.5">QR</span>
            </button>
            <Link
              to="/livret-plateau/print"
              className="inline-flex items-center justify-center w-9 h-9 sm:w-auto sm:px-3 rounded-lg bg-red-600 hover:bg-red-700 text-white text-[12px] font-bold shadow-md transition"
              data-testid="btn-download-pdf"
              aria-label="Ouvrir la version PDF imprimable"
            >
              <Download size={13} /> <span className="hidden sm:inline sm:ml-1.5">PDF</span>
            </Link>
          </div>
        </div>
        {/* Barre de progression fine */}
        <div className="h-1 bg-white/[0.05]">
          <div
            className="h-full transition-all duration-300"
            style={{
              width: `${((chapterIdx) / (CHAPTERS.length - 1)) * 100}%`,
              background: `linear-gradient(90deg, ${current.color} 0%, #f97316 100%)`,
            }}
          />
        </div>
        {/* Titre chapitre sur mobile (masqué depuis header pour compacter) */}
        <div className="md:hidden px-4 py-2 bg-black/40 border-b border-white/5">
          <div className="text-[9.5px] font-black uppercase tracking-[0.2em] text-white/60 truncate">
            {current.n && <span className="text-red-400">{current.n} · </span>}{current.title}
          </div>
        </div>
      </header>

      {/* Contenu chapitre */}
      <main className="max-w-3xl mx-auto px-4 py-8 pb-32">
        {current.id === 'cover'  && <ChapterCover profil={profil} onSave={saveProfil} />}
        {current.id === 'ch01' && <Chapter01 state={state} setValue={setValue} />}
        {current.id === 'ch02' && <Chapter02 state={state} setValue={setValue} />}
        {current.id === 'ch03' && <Chapter03 state={state} setValue={setValue} />}
        {current.id === 'ch04' && <Chapter04 state={state} setValue={setValue} />}
        {current.id === 'ch05' && <Chapter05 state={state} setValue={setValue} />}
        {current.id === 'ch06' && <Chapter06 state={state} setValue={setValue} />}
        {current.id === 'ch07' && <Chapter07 state={state} setValue={setValue} />}

        {/* CTA « Chapitre complet → passer au suivant » — apparaît automatiquement
            quand tous les items du chapitre sont cochés, jusqu'à ce que l'élève
            l'ait fermé (croix) ou soit passé au chapitre suivant. */}
        {showNextChapterCTA && (
          <div
            className="mt-8 rounded-3xl border-2 border-emerald-500/60 bg-gradient-to-br from-emerald-500/15 via-emerald-500/5 to-transparent p-5 sm:p-6 relative"
            data-testid="chapter-complete-cta"
          >
            <button
              type="button"
              onClick={() => setDismissedNext(d => ({ ...d, [current.id]: true }))}
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/30 hover:bg-black/50 border border-white/10 text-white/60 hover:text-white flex items-center justify-center transition"
              aria-label="Fermer la suggestion"
              data-testid="chapter-complete-dismiss"
            >
              <X size={14} />
            </button>
            <div className="flex items-start gap-3 pr-8">
              <div className="w-11 h-11 rounded-xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 size={22} className="text-emerald-300" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-black uppercase tracking-widest text-emerald-300 mb-0.5">
                  Chapitre {current.n} complet · {currentChapterProgress.done} / {currentChapterProgress.total}
                </div>
                <h3 className="text-white font-black text-lg leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  Bravo, tout est validé pour ce chapitre !
                </h3>
                <p className="text-white/70 text-[13px] mt-1.5 leading-relaxed">
                  Tu peux passer au chapitre suivant : <strong className="text-white">{CHAPTERS[chapterIdx + 1]?.n} · {CHAPTERS[chapterIdx + 1]?.title}</strong>.
                </p>
                <button
                  type="button"
                  onClick={next}
                  className="mt-3 inline-flex items-center gap-1.5 h-10 px-4 rounded-xl text-white text-[12.5px] font-black uppercase tracking-widest shadow-lg transition hover:opacity-90"
                  style={{ background: CHAPTERS[chapterIdx + 1]?.color || '#10b981' }}
                  data-testid="chapter-complete-next"
                >
                  Chapitre suivant <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Navigation bas de page */}
      <nav className="fixed bottom-0 left-0 right-0 z-30 bg-neutral-950/95 backdrop-blur border-t border-white/10">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between gap-2">
          <button
            onClick={prev}
            disabled={chapterIdx === 0}
            className="inline-flex items-center gap-1.5 px-4 h-11 rounded-xl bg-white/[0.05] hover:bg-white/[0.12] disabled:opacity-30 disabled:cursor-not-allowed border border-white/10 text-white/85 text-[13px] font-bold transition"
            data-testid="btn-prev"
          >
            <ChevronLeft size={16} /> <span className="hidden sm:inline">Précédent</span>
          </button>
          <div className="flex-1 text-center">
            <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">Chapitre {chapterIdx}/{CHAPTERS.length - 1}</div>
            <div className="text-[11px] text-white/70 font-semibold truncate">{current.title}</div>
          </div>
          <button
            onClick={next}
            disabled={chapterIdx === CHAPTERS.length - 1}
            className="inline-flex items-center gap-1.5 px-4 h-11 rounded-xl text-white text-[13px] font-bold shadow-lg transition disabled:opacity-30 disabled:cursor-not-allowed"
            style={{ background: current.color }}
            data-testid="btn-next"
          >
            <span className="hidden sm:inline">Suivant</span> <ChevronRight size={16} />
          </button>
        </div>
      </nav>

      {/* Sommaire drawer */}
      {menuOpen && (
        <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm" onClick={() => setMenuOpen(false)}>
          <div
            className="absolute top-0 left-0 h-full w-full max-w-sm bg-neutral-900 border-r border-white/10 shadow-2xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-neutral-900 border-b border-white/10 px-5 py-4 flex items-center justify-between">
              <div>
                <div className="text-[9px] font-black uppercase tracking-[0.22em] text-red-400">code2roues.fr</div>
                <div className="text-[16px] font-black text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Sommaire</div>
              </div>
              <button
                onClick={() => setMenuOpen(false)}
                className="p-1.5 rounded hover:bg-white/[0.06] text-white/60 hover:text-white"
                data-testid="btn-close-menu"
                aria-label="Fermer"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-3 space-y-1">
              {CHAPTERS.map((c, i) => {
                const Icon = c.icon;
                const isCurrent = i === chapterIdx;
                const p = perChapterProgress[c.id] || { done: 0, total: 0, complete: false };
                const hasItems = p.total > 0;
                const isComplete = p.complete;
                const hasStarted = p.done > 0;
                return (
                  <button
                    key={c.id}
                    onClick={() => { setChapterIdx(i); setMenuOpen(false); }}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl transition text-left ${
                      isCurrent
                        ? 'bg-white/[0.10] border border-white/20'
                        : 'hover:bg-white/[0.06] border border-transparent'
                    }`}
                    data-testid={`menu-item-${c.id}`}
                  >
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 relative" style={{ background: `${c.color}25`, color: c.color }}>
                      <Icon size={18} />
                      {isComplete && (
                        <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-emerald-500 border-2 border-neutral-900 flex items-center justify-center" title="Chapitre complet">
                          <CheckCircle2 size={10} className="text-white" />
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: c.color }}>
                        {c.n ? `Chapitre ${c.n}` : 'Introduction'}
                      </div>
                      <div className="text-white text-[13px] font-bold leading-tight">{c.title}</div>
                      {/* Badge progression */}
                      {hasItems && (
                        <div className="mt-1.5 flex items-center gap-1.5">
                          <div className="h-1 flex-1 max-w-[80px] rounded-full bg-white/10 overflow-hidden">
                            <div
                              className="h-full transition-all"
                              style={{
                                width: `${Math.round((p.done / p.total) * 100)}%`,
                                background: isComplete ? '#10b981' : c.color,
                              }}
                            />
                          </div>
                          <span
                            className={`text-[10px] font-black tabular-nums ${
                              isComplete ? 'text-emerald-300' : hasStarted ? 'text-white/80' : 'text-white/40'
                            }`}
                            data-testid={`menu-progress-${c.id}`}
                          >
                            {p.done}/{p.total}
                          </span>
                        </div>
                      )}
                    </div>
                    {isCurrent && <div className="w-1.5 h-6 rounded-full" style={{ background: c.color }} />}
                  </button>
                );
              })}
            </div>
            <div className="p-3 border-t border-white/10 mt-2 space-y-2">
              <Link
                to="/cours/premiere-plateau-a2"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white text-[13px] font-black uppercase tracking-wider shadow-lg transition"
                data-testid="drawer-btn-see-full-course-plateau-1"
              >
                <Bike size={14} /> Cours plateau n°1 · Découverte
              </Link>
              <Link
                to="/cours/deuxieme-plateau-a2"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white text-[13px] font-black uppercase tracking-wider shadow-lg transition"
                data-testid="drawer-btn-see-full-course-plateau-2"
              >
                <Bike size={14} /> Cours plateau n°2 · Coordination
              </Link>
              <Link
                to="/cours/troisieme-plateau-a2"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white text-[13px] font-black uppercase tracking-wider shadow-lg transition"
                data-testid="drawer-btn-see-full-course-plateau-3"
              >
                <Bike size={14} /> Cours plateau n°3 · Accélérer & Éviter
              </Link>
              <Link
                to="/cours/quatrieme-plateau-a2"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white text-[13px] font-black uppercase tracking-wider shadow-lg transition"
                data-testid="drawer-btn-see-full-course-plateau-4"
              >
                <Bike size={14} /> Cours plateau n°4 · Bilan final
              </Link>
              <Link
                to="/livret-plateau/print"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white text-[13px] font-bold shadow-lg transition"
                data-testid="drawer-btn-pdf"
              >
                <Download size={14} /> Télécharger le PDF imprimable
              </Link>
              <Link
                to="/livret-circulation"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-300 text-[13px] font-bold transition border border-cyan-500/30"
              >
                <Route size={14} /> Voir aussi le livret circulation
              </Link>
              <div className="text-[10px] text-white/40 text-center mt-2">Version premium · © Bruno Llorens · CODE 2 ROUES 2026</div>
            </div>
          </div>
        </div>
      )}

      {/* Modale QR — partage du livret sur smartphone élève */}
      <LivretQrShareModal
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        livretPath="/livret-plateau"
        livretTitle="Livret Plateau A2"
        livretSubtitle="Progression élève · 7 chapitres"
        accentColor="#dc2626"
        tabletMode
        bypassAuth
      />
    </div>
  );
}

// ═══════ CHAPITRES ═══════════════════════════════════════════════════

function ChapterCover({ profil, onSave }) {
  const [form, setForm] = useState(profil);
  useEffect(() => setForm(profil), [profil]);
  return (
    <div className="animate-fadein">
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-red-600/30 via-orange-500/20 to-transparent p-8 sm:p-12 mb-6">
        {/* Bike SVG background */}
        <div className="absolute -right-16 -bottom-8 opacity-10 pointer-events-none">
          <Bike size={280} strokeWidth={1} />
        </div>
        <div className="relative">
          <div className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.28em] text-red-400 mb-3">
            <Sparkles size={11} /> Version premium
          </div>
          <div className="text-[10px] font-black uppercase tracking-[0.22em] text-white/60 mb-1">code2roues.fr</div>
          <h1 className="text-[36px] sm:text-[52px] font-black text-white leading-[1.05]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Livret élève<br/>
            <span className="text-red-400">Plateau A1/A2</span>
          </h1>
          <div className="mt-3 text-[13px] font-black uppercase tracking-[0.28em] text-white/70">
            PRENDRE EN MAIN · MAÎTRISER · PROGRESSER
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
        <div className="text-[10px] font-black uppercase tracking-[0.22em] text-white/50 mb-3">Ce livret appartient à</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <ProfilField label="Élève" value={form.eleve} onChange={(v) => setForm((f) => ({ ...f, eleve: v }))} onBlur={() => onSave(form)} />
          <ProfilField label="Moniteur / auto-école" value={form.moniteur} onChange={(v) => setForm((f) => ({ ...f, moniteur: v }))} onBlur={() => onSave(form)} />
          <ProfilField label="Début de formation" value={form.debut} onChange={(v) => setForm((f) => ({ ...f, debut: v }))} onBlur={() => onSave(form)} type="date" />
        </div>
      </div>

      <div className="mt-4 text-center text-[11px] text-white/40 italic">
        Support pédagogique CODE 2 ROUES version premium · © Bruno Llorens · 2026
      </div>
    </div>
  );
}

function Chapter01({ state, setValue }) {
  const reperes = [
    { key: 'r-regard',   icon: Eye,     color: '#0ea5e9', title: 'REGARD',      desc: 'Loin, mobile, vers la sortie' },
    { key: 'r-douceur',  icon: Feather, color: '#22c55e', title: 'DOUCEUR',     desc: 'Pas d\'à-coups inutiles' },
    { key: 'r-equilibre', icon: Scale,  color: '#eab308', title: 'ÉQUILIBRE',   desc: 'Corps souple, moto guidée' },
    { key: 'r-regularite', icon: Repeat, color: '#a855f7', title: 'RÉGULARITÉ', desc: 'Répéter pour automatiser' },
  ];
  return (
    <div className="animate-fadein">
      <ChapterHeader n="01" color="#f97316" title="Bienvenue sur le plateau" subtitle="Construire de vrais automatismes" />

      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 mb-6">
        <p className="text-white/80 text-[14px] leading-relaxed">
          Le plateau n&apos;est pas juste une succession de cônes à mémoriser. C&apos;est <strong className="text-orange-400">là que tu construis</strong> ton équilibre, ta précision, ton regard et la maîtrise de tes commandes.
        </p>
      </div>

      <div className="text-[10px] font-black uppercase tracking-[0.22em] text-white/50 mb-3">Comment utiliser ce livret</div>
      <div className="grid grid-cols-3 gap-2 mb-6">
        <StatusExplain label="Découvert" color="#94a3b8" desc="J&apos;ai essayé la compétence" />
        <StatusExplain label="En cours" color="#f59e0b" desc="Je la travaille, je la stabilise" />
        <StatusExplain label="Acquis" color="#10b981" desc="Mon moniteur valide" />
      </div>

      <div className="text-[10px] font-black uppercase tracking-[0.22em] text-white/50 mb-3">4 repères à garder en tête</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
        {reperes.map((r) => (
          <RepereCard key={r.key} r={r} checked={!!state[r.key]} onToggle={() => setValue(r.key, !state[r.key])} />
        ))}
      </div>

      <blockquote className="rounded-2xl border-l-4 border-red-500 bg-red-500/10 p-5">
        <div className="text-[14px] font-bold text-white leading-relaxed italic">
          « Chaque passage te donne une information. »
        </div>
        <div className="text-[12px] text-white/60 mt-1">Cherche à comprendre ce qui change sur la moto, puis recommence.</div>
      </blockquote>
    </div>
  );
}

function Chapter02({ state, setValue }) {
  const equipement = [
    { key: 'e-casque', label: 'CASQUE',     desc: 'Homologué, attaché et ajusté' },
    { key: 'e-blouson', label: 'BLOUSON',   desc: 'Protection épaules/coudes/dos' },
    { key: 'e-gants',  label: 'GANTS',      desc: 'Homologués et adaptés' },
    { key: 'e-pantalon', label: 'PANTALON', desc: 'Protection adaptée à la pratique' },
    { key: 'e-chaussures', label: 'CHAUSSURES', desc: 'Montantes, malléoles protégées' },
  ];
  const commandes = [
    { key: 'c-mg', label: 'MAIN GAUCHE',   desc: 'Embrayage' },
    { key: 'c-pg', label: 'PIED GAUCHE',   desc: 'Sélecteur de vitesses' },
    { key: 'c-md', label: 'MAIN DROITE',   desc: 'Accélérateur + frein avant' },
    { key: 'c-pd', label: 'PIED DROIT',    desc: 'Frein arrière' },
  ];
  const appuis = [
    { n: 1, label: 'Pieds', desc: 'Sur les repose-pieds, pas les talons' },
    { n: 2, label: 'Genoux', desc: 'Serrés contre le réservoir' },
    { n: 3, label: 'Bassin', desc: 'Ancré dans la selle' },
    { n: 4, label: 'Mains', desc: 'Relâchées sur les poignées' },
    { n: 5, label: 'Regard', desc: 'Haut et loin' },
  ];
  return (
    <div className="animate-fadein">
      <ChapterHeader n="02" color="#eab308" title="Moto · Équipement · Commandes" subtitle="Bien équipé, bien installé, prêt à travailler" />

      <SectionTitle>Ton équipement (5 éléments)</SectionTitle>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
        {equipement.map((e) => (
          <CheckItem key={e.key} label={e.label} desc={e.desc} checked={!!state[e.key]} onToggle={() => setValue(e.key, !state[e.key])} color="#eab308" />
        ))}
      </div>

      <SectionTitle>Les 4 commandes à identifier sans hésiter</SectionTitle>
      <div className="grid grid-cols-2 gap-2 mb-6">
        {commandes.map((c) => (
          <CheckItem key={c.key} label={c.label} desc={c.desc} checked={!!state[c.key]} onToggle={() => setValue(c.key, !state[c.key])} color="#f97316" />
        ))}
      </div>

      <SectionTitle>5 points d&apos;appui = 1 seul corps avec la moto</SectionTitle>
      <div className="grid grid-cols-5 gap-2 mb-6">
        {appuis.map((a) => (
          <div key={a.n} className="rounded-xl border border-white/10 bg-white/[0.03] p-3 text-center">
            <div className="w-9 h-9 mx-auto rounded-full bg-yellow-500/25 text-yellow-400 flex items-center justify-center text-[14px] font-black mb-1.5">
              {a.n}
            </div>
            <div className="text-[11.5px] font-black text-white">{a.label}</div>
            <div className="text-[9.5px] text-white/50 leading-tight mt-0.5">{a.desc}</div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
        <div className="text-[10px] font-black uppercase tracking-widest text-yellow-400 mb-1">💡 Prise des commandes</div>
        <p className="text-white/80 text-[12.5px] leading-relaxed">
          Mains souples sur les poignées, sans crispation. Les doigts viennent actionner les commandes lorsque nécessaire : <strong>aucun nombre de doigts imposé</strong>.
        </p>
      </div>
    </div>
  );
}

function Chapter03({ state, setValue }) {
  const opt = state['parcours-memoire'] || null;
  return (
    <div className="animate-fadein">
      <ChapterHeader n="03" color="#22c55e" title="Ton parcours plateau" subtitle="À toi de le dessiner" />

      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 mb-4">
        <p className="text-white/80 text-[13px] leading-relaxed">
          Dessine le parcours que tu vas apprendre : place les cônes, le départ, l&apos;arrivée et indique le sens de circulation. Le refaire de mémoire est déjà un exercice de préparation.
        </p>
      </div>

      {/* Canvas dessinable stylisé (SVG grille) */}
      <div className="rounded-2xl border-2 border-green-500/30 bg-white/[0.02] p-4 mb-4">
        <div className="text-[9.5px] font-black uppercase tracking-widest text-green-400 mb-2">🎨 Zone de dessin (support papier recommandé)</div>
        <svg viewBox="0 0 400 200" className="w-full h-auto" role="img" aria-label="Grille de dessin plateau">
          <defs>
            <pattern id="grid-plateau" width="20" height="20" patternUnits="userSpaceOnUse">
              <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#ffffff10" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="400" height="200" fill="url(#grid-plateau)" />
          {/* Exemples : départ / arrivée */}
          <rect x="10" y="90" width="30" height="20" fill="#22c55e40" stroke="#22c55e" strokeWidth="1" />
          <text x="25" y="105" textAnchor="middle" fontSize="8" fontWeight="700" fill="#22c55e">DÉPART</text>
          <rect x="360" y="90" width="30" height="20" fill="#ef444440" stroke="#ef4444" strokeWidth="1" />
          <text x="375" y="105" textAnchor="middle" fontSize="8" fontWeight="700" fill="#ef4444">ARRIVÉE</text>
          {/* Cônes indicatifs */}
          {[80, 130, 180, 230, 280, 330].map((x, i) => (
            <circle key={i} cx={x} cy={i % 2 === 0 ? 80 : 120} r="4" fill="#f97316" opacity="0.4" />
          ))}
          <text x="200" y="180" textAnchor="middle" fontSize="10" fontStyle="italic" fill="#ffffff30">Dessine ton parcours ici (sur ta copie papier)</text>
        </svg>
      </div>

      <div className="rounded-2xl border-l-4 border-green-500 bg-green-500/10 p-4 mb-6">
        <div className="text-[10px] font-black uppercase tracking-widest text-green-400 mb-1">💡 Conseil moniteur</div>
        <p className="text-white/80 text-[12.5px] leading-relaxed">
          Commence par le tracer de mémoire. Corrige ensuite avec ton enseignant. <strong>Quand tu sais expliquer le parcours, tu commences déjà à mieux le piloter.</strong>
        </p>
      </div>

      <SectionTitle>Je sais le refaire de mémoire</SectionTitle>
      <div className="grid grid-cols-3 gap-2">
        {[
          { id: 'oui', label: 'OUI', color: '#10b981' },
          { id: 'presque', label: 'PRESQUE', color: '#f59e0b' },
          { id: 'arevoir', label: 'À REVOIR', color: '#ef4444' },
        ].map((o) => (
          <button
            key={o.id}
            onClick={() => setValue('parcours-memoire', opt === o.id ? null : o.id)}
            className={`px-3 py-4 rounded-xl border-2 text-[12px] font-black transition ${
              opt === o.id ? 'text-white shadow-lg' : 'text-white/70 border-white/10 bg-white/[0.03] hover:bg-white/[0.08]'
            }`}
            style={opt === o.id ? { background: o.color, borderColor: o.color } : {}}
            data-testid="parcours-opt"
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function ManoeuvreCard({ n, title, actions, color, state, setValue }) {
  const key = `m-${n}`;
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 mb-3">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-[14px] font-black shrink-0" style={{ background: color }}>
          {n}
        </div>
        <div className="text-white text-[14px] font-black" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Manœuvre {n} — {title}
        </div>
      </div>
      <ul className="space-y-1.5 mb-3 pl-2">
        {actions.map((a, i) => (
          <li key={i} className="text-white/80 text-[12.5px] flex items-start gap-2">
            <span className="text-lg leading-none" style={{ color }}>›</span>
            <span>{a}</span>
          </li>
        ))}
      </ul>
      <div className="border-t border-white/10 pt-2 flex items-center justify-between">
        <div className="text-[9.5px] font-black uppercase tracking-widest" style={{ color }}>
          À RETENIR : précision · regard · régularité
        </div>
        <ToggleAcquis checked={!!state[key]} onToggle={() => setValue(key, !state[key])} color={color} />
      </div>
    </div>
  );
}

function Chapter04({ state, setValue }) {
  return (
    <div className="animate-fadein">
      <ChapterHeader n="04" color="#06b6d4" title="Manœuvres 1 à 3" subtitle="Manipulation · allure lente · freinage" />
      <ManoeuvreCard n="01" title="Sans l&apos;aide du moteur" color="#06b6d4" state={state} setValue={setValue}
        actions={['Maîtriser le poids de la moto', 'Déplacer et rebéquiller proprement']} />
      <ManoeuvreCard n="02" title="Allure réduite" color="#0891b2" state={state} setValue={setValue}
        actions={['Regard mobile et loin', 'Patinage stable + frein arrière']} />
      <ManoeuvreCard n="03" title="Freinage d&apos;urgence" color="#0e7490" state={state} setValue={setValue}
        actions={['Moto droite et regard loin', 'Freinage progressif et précis']} />
    </div>
  );
}

function Chapter05({ state, setValue }) {
  return (
    <div className="animate-fadein">
      <ChapterHeader n="05" color="#6366f1" title="Manœuvres 4 à 6" subtitle="Passager · slalom · évitement" />
      <ManoeuvreCard n="04" title="Allure réduite avec passager" color="#6366f1" state={state} setValue={setValue}
        actions={['Anticiper le changement d&apos;équilibre', 'Rester progressif dans les commandes']} />
      <ManoeuvreCard n="05" title="Slalom" color="#4f46e5" state={state} setValue={setValue}
        actions={['Trajectoire fluide', 'Bras souples, regard vers la sortie']} />
      <ManoeuvreCard n="06" title="Évitement" color="#4338ca" state={state} setValue={setValue}
        actions={['Déclencher franchement', 'Redresser puis stabiliser']} />
    </div>
  );
}

function Chapter06({ state, setValue }) {
  const competences = [
    'Regard', 'Position de conduite', 'Manipulation à l&apos;arrêt', 'Commandes moto',
    'Point de patinage', 'Allure lente', 'Freinage d&apos;urgence', 'Passager',
    'Slalom', 'Évitement', 'Enchaînement complet', 'Préparation examen',
  ];
  const statuses = [
    { id: 'none', label: 'Non vu', color: '#94a3b8' },
    { id: 'decouvert', label: 'Découvert', color: '#94a3b8' },
    { id: 'encours', label: 'En cours', color: '#f59e0b' },
    { id: 'acquis', label: 'Acquis', color: '#10b981' },
  ];

  // ─── Sync auto-évaluation trame → livret ch06 ─────────────────────
  // On lit les auto-évaluations locales (localStorage) faites sur la
  // trame Plateau, on les convertit en statuts livret et on propose à
  // l'élève de pré-remplir OU fusionner. On respecte son choix via
  // `state.__ch06_autoEvalDismissed` pour ne pas re-spammer la bannière.
  const autoEvalUpdates = React.useMemo(() => {
    const raw = loadPlateauAutoEval();
    return computeProgFromAutoEval(raw);
  }, [state.__ch06_autoEvalStamp]); // recalcule au retour depuis la trame

  const stats = React.useMemo(() => summarizeProgUpdates(autoEvalUpdates), [autoEvalUpdates]);

  // Détecte les items du livret déjà remplis manuellement (ceux qui
  // pourraient être écrasés). Sert à afficher un avertissement.
  const conflictKeys = Object.keys(autoEvalUpdates).filter((k) => {
    const cur = state[k];
    return cur && cur !== 'none' && cur !== autoEvalUpdates[k];
  });

  const hasAnyManualFill = Object.keys(autoEvalUpdates).some((k) => {
    const cur = state[k];
    return cur && cur !== 'none';
  });

  const showBanner =
    stats.total > 0 &&
    !state.__ch06_autoEvalDismissed;

  const applyMerge = () => {
    // Fusionner : n'écrase pas les items déjà remplis manuellement,
    // n'ajoute que ceux encore vides ('none' ou undefined).
    Object.entries(autoEvalUpdates).forEach(([key, newVal]) => {
      const cur = state[key];
      if (!cur || cur === 'none') setValue(key, newVal);
    });
    setValue('__ch06_autoEvalDismissed', 'merged');
    setValue('__ch06_autoEvalStamp', new Date().toISOString());
  };
  const applyReplace = () => {
    // Remplacer : écrase avec la donnée auto-éval la plus récente.
    Object.entries(autoEvalUpdates).forEach(([key, newVal]) => {
      setValue(key, newVal);
    });
    setValue('__ch06_autoEvalDismissed', 'replaced');
    setValue('__ch06_autoEvalStamp', new Date().toISOString());
  };
  const applyDismiss = () => {
    setValue('__ch06_autoEvalDismissed', 'ignored');
  };

  return (
    <div className="animate-fadein">
      <ChapterHeader n="06" color="#a855f7" title="Ma progression" subtitle="Une lecture simple de ce qui est acquis" />

      {/* Bannière de proposition — n'apparaît que si de la data auto-éval
          existe ET que l'élève n'a pas encore choisi (fusion, remplacement,
          ou ignoré). Cf. utils/livretAutoEvalMap.js */}
      {showBanner && (
        <div
          className="mb-5 rounded-3xl border-2 border-emerald-500/40 bg-gradient-to-br from-emerald-500/12 via-emerald-500/5 to-transparent p-4 sm:p-5 relative"
          data-testid="ch06-autoeval-sync-banner"
        >
          <button
            type="button"
            onClick={applyDismiss}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/30 hover:bg-black/50 border border-white/10 text-white/60 hover:text-white flex items-center justify-center transition"
            aria-label="Ignorer la proposition"
            data-testid="ch06-autoeval-sync-dismiss"
          >
            <X size={14} />
          </button>
          <div className="flex items-start gap-3 pr-8">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center flex-shrink-0">
              <Sparkles size={18} className="text-emerald-300" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-black uppercase tracking-widest text-emerald-300 mb-0.5">
                Auto-évaluation détectée · {stats.total} compétence{stats.total > 1 ? 's' : ''}
              </div>
              <h3 className="text-white font-black text-[15px] sm:text-[16px] leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                Pré-remplir ta progression depuis la trame Plateau ?
              </h3>
              <p className="text-white/70 text-[12.5px] mt-1 leading-relaxed">
                Répartition détectée : <span className="text-emerald-300 font-bold">{stats.acquis} acquis</span> · <span className="text-amber-300 font-bold">{stats.encours} en cours</span> · <span className="text-white/60 font-bold">{stats.decouvert} découverts</span>.
              </p>
              {conflictKeys.length > 0 && hasAnyManualFill && (
                <p className="mt-1.5 text-amber-200/85 text-[11.5px] leading-relaxed" data-testid="ch06-autoeval-conflict-warning">
                  <strong>Attention :</strong> tu as déjà rempli {conflictKeys.length} ligne{conflictKeys.length > 1 ? 's' : ''} à la main. Choisis <em>Fusionner</em> pour garder tes saisies, ou <em>Remplacer</em> pour tout écraser avec l'auto-éval.
                </p>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={applyMerge}
                  className="inline-flex items-center gap-1.5 h-10 px-3.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white text-[12px] font-black uppercase tracking-widest shadow-lg transition"
                  data-testid="ch06-autoeval-sync-merge"
                >
                  <CheckCircle2 size={14} /> Fusionner (ne pas écraser)
                </button>
                <button
                  type="button"
                  onClick={applyReplace}
                  className="inline-flex items-center gap-1.5 h-10 px-3.5 rounded-xl bg-white/10 hover:bg-white/15 border border-white/15 text-white text-[12px] font-black uppercase tracking-widest transition"
                  data-testid="ch06-autoeval-sync-replace"
                >
                  <Repeat size={14} /> Remplacer par l'auto-éval
                </button>
                <button
                  type="button"
                  onClick={applyDismiss}
                  className="inline-flex items-center gap-1.5 h-10 px-3.5 rounded-xl bg-transparent hover:bg-white/[0.05] border border-white/10 text-white/60 hover:text-white/85 text-[12px] font-bold transition"
                  data-testid="ch06-autoeval-sync-ignore"
                >
                  Plus tard
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden mb-4">
        {competences.map((c, i) => {
          const key = `prog-${i}`;
          const cur = state[key] || 'none';
          return (
            <div key={i} className={`px-3 py-2.5 flex items-center gap-2 ${i !== competences.length - 1 ? 'border-b border-white/5' : ''}`}>
              <div className="flex-1 min-w-0 text-white text-[12.5px] font-bold" dangerouslySetInnerHTML={{ __html: c }} />
              <div className="flex items-center gap-1">
                {statuses.slice(1).map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setValue(key, cur === s.id ? 'none' : s.id)}
                    className={`px-2 py-1 rounded text-[9.5px] font-black uppercase tracking-widest transition ${
                      cur === s.id ? 'text-white' : 'bg-white/[0.03] text-white/50 border border-white/10 hover:bg-white/[0.08]'
                    }`}
                    style={cur === s.id ? { background: s.color } : {}}
                    data-testid="prog-status"
                  >
                    {s.label[0]}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Sync inverse : Livret → Trame (pré-remplir l'auto-éval de la prochaine séance) */}
      <SyncLivretToTrameCard state={state} />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <TextArea label="Mon point fort du moment" value={state.pointfort || ''} onChange={(v) => setValue('pointfort', v)} />
        <TextArea label="Mon objectif pour la prochaine leçon" value={state.objectif || ''} onChange={(v) => setValue('objectif', v)} />
      </div>
    </div>
  );
}

/**
 * Bouton « Envoyer les compétences acquises vers ma trame Plateau ».
 * Le rendu s'affiche uniquement si l'élève a au moins 1 compétence marquée « acquis ».
 * Après l'envoi, un toast récapitule ce qui a été mis à jour (n° d'exercices + phases).
 */
function SyncLivretToTrameCard({ state }) {
  const progressDict = React.useMemo(() => {
    const out = {};
    for (let i = 0; i < 12; i++) {
      const v = state[`prog-${i}`];
      if (v === 'acquis' || v === 'encours' || v === 'decouvert') out[`prog-${i}`] = v;
    }
    return out;
  }, [state]);

  const preview = React.useMemo(() => previewLivretToTrameSync(progressDict), [progressDict]);
  if (preview.acquiredCount === 0) return null;

  const handleSync = () => {
    const result = applyLivretToPlateauAutoEval(progressDict, { onlyAcquired: true, overwriteOk: false });
    if (result.updated === 0 && result.skipped === 0) {
      toast.info('Aucune trame Plateau à mettre à jour', {
        description: 'Ouvre une trame P1, P2 ou P3 en mode élève pour créer les auto-évals.',
      });
      return;
    }
    const phases = result.phases.length > 0 ? ` sur ${result.phases.join(', ').toUpperCase()}` : '';
    toast.success(`${result.updated} exercice${result.updated > 1 ? 's' : ''} pré-rempli${result.updated > 1 ? 's' : ''}${phases}`, {
      description: result.skipped > 0
        ? `${result.skipped} déjà validé${result.skipped > 1 ? 's' : ''} — non modifié${result.skipped > 1 ? 's' : ''}.`
        : 'Ta prochaine séance est prête à démarrer.',
      duration: 5000,
    });
  };

  return (
    <div
      className="mb-4 rounded-3xl border-2 border-sky-500/40 bg-gradient-to-br from-sky-500/12 via-sky-500/5 to-transparent p-4 sm:p-5"
      data-testid="livret-to-trame-sync-card"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-sky-500/20 border border-sky-500/40 flex items-center justify-center flex-shrink-0">
          <Repeat size={18} className="text-sky-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-black uppercase tracking-widest text-sky-300 mb-0.5">
            Sync livret → trame pédagogique
          </div>
          <h3 className="text-white font-black text-[15px] sm:text-[16px] leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Pré-remplir mon auto-éval de la prochaine séance ?
          </h3>
          <p className="text-white/70 text-[12.5px] mt-1 leading-relaxed">
            Tu as validé <strong className="text-sky-300">{preview.acquiredCount} compétence{preview.acquiredCount > 1 ? 's' : ''}</strong> comme <em>acquise{preview.acquiredCount > 1 ? 's' : ''}</em>.
            En un clic, tous les exercices correspondants passeront à <strong className="text-emerald-300">✅ ok</strong> sur ta trame Plateau.
            <br className="hidden sm:inline" />
            <span className="text-white/50">Les exercices déjà à « ok » ne sont pas modifiés.</span>
          </p>
          <button
            type="button"
            onClick={handleSync}
            className="mt-3 inline-flex items-center gap-1.5 h-10 px-3.5 rounded-xl bg-sky-500 hover:bg-sky-400 text-white text-[12px] font-black uppercase tracking-widest shadow-lg transition"
            data-testid="livret-to-trame-sync-btn"
          >
            <CheckCircle2 size={14} /> Envoyer vers ma trame
          </button>
        </div>
      </div>
    </div>
  );
}

function Chapter07({ state, setValue }) {
  const checks = [
    { key: 'x-ordre',    label: 'Je connais l\'ordre des 6 manœuvres' },
    { key: 'x-regard',   label: 'Je garde mon regard loin et mobile' },
    { key: 'x-cmds',     label: 'Je maîtrise mes commandes sans précipitation' },
    { key: 'x-focus',    label: 'Je reste concentré sur une zone à la fois' },
    { key: 'x-respire',  label: 'Je respire et je repars après une erreur' },
  ];
  const done = checks.filter((c) => !!state[c.key]).length;
  return (
    <div className="animate-fadein">
      <ChapterHeader n="07" color="#ec4899" title="Prêt pour l'examen ?" subtitle="Le jour J : simple, concentré, régulier" />

      <div className="text-[10px] font-black uppercase tracking-[0.22em] text-white/50 mb-3">Ma check-list examen ({done}/{checks.length})</div>
      <div className="space-y-2 mb-6">
        {checks.map((c) => (
          <CheckExamen key={c.key} label={c.label} checked={!!state[c.key]} onToggle={() => setValue(c.key, !state[c.key])} />
        ))}
      </div>

      <div className="relative overflow-hidden rounded-3xl border border-pink-500/30 bg-gradient-to-br from-pink-500/20 via-rose-500/10 to-transparent p-6 mb-6">
        <div className="text-[10px] font-black uppercase tracking-[0.22em] text-pink-400 mb-2">✨ Le mot de la fin</div>
        <div className="text-[22px] sm:text-[28px] font-black text-white leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Le but n&apos;est pas d&apos;être parfait.
        </div>
        <p className="text-white/80 text-[13.5px] leading-relaxed mt-2">
          Le but est d&apos;être <strong className="text-pink-300">assez régulier</strong> pour reproduire ce que tu sais faire.
        </p>
        <div className="mt-4 text-[15px] font-bold italic text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          « Fais-toi confiance. Une manœuvre à la fois. »
        </div>
      </div>

      {done === checks.length && (
        <div className="rounded-2xl border-2 border-emerald-500 bg-emerald-500/10 p-5 text-center">
          <Trophy size={40} className="mx-auto text-emerald-400 mb-2" />
          <div className="text-[16px] font-black text-emerald-300" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Tu es prêt(e) 🎉
          </div>
          <div className="text-[12px] text-white/70 mt-1">Toutes les cases sont cochées. Va décrocher ton plateau !</div>
        </div>
      )}

      <div className="mt-6 text-center text-[10px] text-white/40 italic">
        CODE 2 ROUES · Plus qu&apos;un code, une maîtrise à construire · © Bruno Llorens 2026
      </div>
    </div>
  );
}

// ═══════ Sous-composants ═══════════════════════════════════════════

function ChapterHeader({ n, color, title, subtitle }) {
  return (
    <div className="mb-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="text-[38px] sm:text-[52px] font-black leading-none" style={{ fontFamily: "'Playfair Display', Georgia, serif", color }}>
          {n}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-black uppercase tracking-[0.22em]" style={{ color }}>Chapitre</div>
          <div className="text-[20px] sm:text-[24px] font-black text-white leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            {title}
          </div>
        </div>
      </div>
      <div className="text-[13px] text-white/60 italic pl-1">{subtitle}</div>
      <div className="h-1 rounded-full mt-3" style={{ background: `linear-gradient(90deg, ${color}, transparent)` }} />
    </div>
  );
}

function SectionTitle({ children }) {
  return <div className="text-[10px] font-black uppercase tracking-[0.22em] text-white/50 mb-2 mt-4">{children}</div>;
}

function ProfilField({ label, value, onChange, onBlur, type = 'text' }) {
  return (
    <label className="block">
      <div className="text-[9px] font-black uppercase tracking-widest text-white/40 mb-1">{label}</div>
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className="w-full bg-white/[0.05] border border-white/10 rounded-lg px-3 py-2 text-white text-[13px] focus:outline-none focus:border-red-500/60 transition"
        data-testid={`profil-${label}`}
      />
    </label>
  );
}

function StatusExplain({ label, color, desc }) {
  return (
    <div className="rounded-xl border p-3" style={{ background: `${color}12`, borderColor: `${color}40` }}>
      <div className="text-[10.5px] font-black uppercase tracking-widest mb-0.5" style={{ color }}>{label}</div>
      <div className="text-[11px] text-white/70 leading-tight" dangerouslySetInnerHTML={{ __html: desc }} />
    </div>
  );
}

function RepereCard({ r, checked, onToggle }) {
  const Icon = r.icon;
  return (
    <button
      onClick={onToggle}
      className={`rounded-2xl border-2 p-4 text-left transition ${
        checked ? 'shadow-lg' : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.08]'
      }`}
      style={checked ? { borderColor: r.color, background: `${r.color}15` } : {}}
      data-testid="repere-card"
    >
      <div className="flex items-center gap-2 mb-1.5">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${r.color}25`, color: r.color }}>
          <Icon size={17} />
        </div>
        <div className="text-[14px] font-black text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          {r.title}
        </div>
        <div className="ml-auto">
          {checked ? <CheckCircle2 size={18} style={{ color: r.color }} /> : <Circle size={18} className="text-white/30" />}
        </div>
      </div>
      <div className="text-[12px] text-white/70 leading-snug pl-11">{r.desc}</div>
    </button>
  );
}

function CheckItem({ label, desc, checked, onToggle, color }) {
  return (
    <button
      onClick={onToggle}
      className={`flex items-start gap-2 p-3 rounded-xl border-2 text-left transition ${
        checked ? 'shadow-md' : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.08]'
      }`}
      style={checked ? { borderColor: color, background: `${color}12` } : {}}
      data-testid="check-item"
    >
      {checked ? <CheckCircle2 size={18} style={{ color }} className="mt-0.5 shrink-0" /> : <Circle size={18} className="text-white/30 mt-0.5 shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className="text-[11.5px] font-black text-white leading-tight">{label}</div>
        <div className="text-[10.5px] text-white/60 leading-tight mt-0.5">{desc}</div>
      </div>
    </button>
  );
}

function ToggleAcquis({ checked, onToggle, color }) {
  return (
    <button
      onClick={onToggle}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest transition ${
        checked ? 'text-white shadow-md' : 'bg-white/[0.05] text-white/50 border border-white/10 hover:bg-white/[0.10]'
      }`}
      style={checked ? { background: color } : {}}
      data-testid="toggle-acquis"
    >
      {checked ? <><CheckCircle2 size={11} /> Acquis</> : <><Circle size={11} /> Marquer acquis</>}
    </button>
  );
}

function TextArea({ label, value, onChange }) {
  return (
    <label className="block">
      <div className="text-[10px] font-black uppercase tracking-widest text-white/50 mb-1">{label}</div>
      <textarea
        rows={3}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white/[0.05] border border-white/10 rounded-lg px-3 py-2 text-white text-[12.5px] focus:outline-none focus:border-purple-500/60 transition resize-none"
        placeholder="Écris ici…"
      />
    </label>
  );
}

function CheckExamen({ label, checked, onToggle }) {
  return (
    <button
      onClick={onToggle}
      className={`w-full flex items-center gap-3 p-3.5 rounded-xl border-2 text-left transition ${
        checked ? 'border-pink-500 bg-pink-500/15 shadow-md' : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.08]'
      }`}
      data-testid="check-examen"
    >
      {checked
        ? <CheckCircle2 size={22} className="text-pink-400 shrink-0" />
        : <Circle size={22} className="text-white/30 shrink-0" />
      }
      <span className={`text-[13px] font-bold leading-tight ${checked ? 'text-white' : 'text-white/70'}`}>{label}</span>
    </button>
  );
}
